import React from 'react';
import { X, AlertCircle, FileText } from 'lucide-react';
import { formatFileSize, MAX_FILE_SIZE } from '@/utils/fileSizeValidation';

interface FileSizeErrorModalProps {
  fileName: string;
  fileSize: number;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal component to display file size error with clear messaging
 */
const FileSizeErrorModal: React.FC<FileSizeErrorModalProps> = ({
  fileName,
  fileSize,
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  const fileSizeFormatted = formatFileSize(fileSize);
  const maxSizeFormatted = formatFileSize(MAX_FILE_SIZE);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">File Too Large</h3>
              <p className="text-sm text-gray-500">Maximum file size exceeded</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <FileText className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate" title={fileName}>
                {fileName}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                File size: <span className="font-medium text-red-600">{fileSizeFormatted}</span>
              </p>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-700">
              This file exceeds the maximum allowed size of{' '}
              <span className="font-semibold text-gray-900">{maxSizeFormatted}</span>.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-900 mb-2">What you can do:</p>
            <ul className="text-sm text-blue-800 space-y-1.5 list-disc list-inside">
              <li>Compress the file to reduce its size</li>
              <li>Split the file into smaller parts</li>
              <li>Choose a different, smaller file</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileSizeErrorModal;

